import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import {
  DateFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  FileFieldProps,
  FileRepresentation,
  SelectFieldProps,
  SelectOption,
} from '../../../../components/form-builder/form-builder.types';
import { AgpmRepository } from '../../../../store/agpm-state/agpm-state.repository';
import { ResponseDoc } from '@/types';

interface getCreateAgpmFormObjectProps {
  store: AgpmRepository;
  acOptions: SelectOption[];
  download?: (file: FileRepresentation) => void;
  resetTmpFiles?: (resetFn: () => void) => void;
}

export const getCreateAgpmFormObject = ({ store, download, acOptions, resetTmpFiles }: getCreateAgpmFormObjectProps): DynamicForm => {
  const acOptionsField: DynamicField<SelectFieldProps> = {
    label: 'agpm.form.acOptions.title',
    variant: FieldVariant.SELECT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'acOptions'),
    error: store.getNestedObservable<string>('errors', 'acId'),
    isRequired: true,
    props: {
      value: store.getObservable('acsSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('acsSelected', value);
        store.setNested('dto', 'acId', value?.code);
      },
      placeholder: 'agpm.form.acOptions.placeholder',
      options: acOptions,
      filterEnabled: true,
    },
  };

  const yearField: DynamicField<DateFieldProps> = {
    label: 'agpm.form.year.label',
    variant: FieldVariant.DATE,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'annee'),
    props: {
      value: store.getNestedObservable<number>('dto', 'annee'),
      onChange: (value: Date) => {
        store.setNested('dto', 'annee', value.getFullYear().toString());
      },
      view: 'year',
      format: 'yy',
      minDate: new Date(new Date().getFullYear(), 0, 1),
      lastDate: new Date(new Date().getFullYear() + 1, 11, 31),
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  let uploaderReset: () => void = () => {};
  const uploadField: DynamicField<FileFieldProps> = {
    label: 'agpm.form.files.label',
    variant: FieldVariant.FILE,
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable<File[]>('dto', 'files'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('dto', 'files', event.currentFiles);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'dto',
          'files',
          store.getNested<File[]>('dto', 'files').filter(file => file.name !== event.file.name)
        );
      },
      onFileRepresentationChange: (file: FileRepresentation) => {
        store.setNested(
          'response',
          'docs',
          store.getNested<ResponseDoc[]>('response', 'docs').filter(doc => doc.id != file.id)
        );
        store.setNested(
          'dto',
          'docIds',
          store.getNested<number[]>('dto', 'docIds').filter(id => id !== file.id)
        );
      },
      onFileDownLoad: (file: FileRepresentation) => download?.(file),
      setResetMethod: (fn: () => void) => {
        uploaderReset = fn;
        resetTmpFiles?.(fn);
      },
    },
  };

  return {
    title: 'agpm.form.addTitle',
    description: 'agpm.form.addDescription',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: 'Information Avis',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [yearField, acOptionsField],
          },
          {
            fields: [
              {
                label: 'agpm.form.nom.label',
                variant: FieldVariant.TEXT,
                isRequired: true,
                error: store.getNestedObservable<string>('errors', 'nom'),
                props: {
                  value: store.getNestedObservable<string>('dto', 'nom'),
                  onChange: (value: string) => {
                    store.setNested('dto', 'nom', value);
                  },
                },
              },
            ],
          },
          {
            fields: [
              {
                label: 'agpm.form.contenu.label',
                variant: FieldVariant.EDITOR,
                isRequired: true,
                error: store.getNestedObservable('errors', 'contenu'),
                props: {
                  height: '320px',
                  value: store.getNestedObservable<string>('dto', 'contenu'),
                  onChange: (value: string) => {
                    store.setNested('dto', 'contenu', value);
                  },
                },
              },
            ],
          },
        ],
      },
      {
        title: 'agpm.form.secondaryEntries.label',
        gridItems: [
          {
            fields: [uploadField],
          },
          {
            fields: [
              {
                label: 'agpm.form.remarque.label',
                variant: FieldVariant.EDITOR,
                isRequired: false,
                error: store.getNestedObservable<string>('errors', 'remarque'),
                props: {
                  height: '320px',
                  value: store.getNestedObservable<string>('dto', 'remarque'),
                  onChange: (value: string) => {
                    store.setNested('dto', 'remarque', value);
                  },
                },
              },
            ],
          },
        ],
      },
    ],
  };
};
